/**
 * One-shot, loop or song (2026-10-13, the audio library; operator: "how do we differentiate between a one shot
 * sample, a loop, and a whole song?").
 *
 * What the operator said wins (`kind`, kept by OPBridge once it has the audio library). Otherwise a guess from what
 * the analysis already gives, the way Live guesses when a sample is dropped:
 *  - a ONE-SHOT is short (under 10 s), or has no steady beat;
 *  - a LOOP keeps a steady tempo, starts on the downbeat and is a whole number of bars (1, 2, 4, 8, 16 or 32), up to
 *    a minute;
 *  - a SONG is anything longer with a steady beat.
 * Pure: the store passes in the track and its beat map.
 */
import type { AudioKind, TestTrack, TestTrackBeatmap } from '../opbridge/httpClient';
import { barSeconds } from './testTrack';

export const KIND_LABEL: Record<AudioKind, string> = { oneshot: 'One-shot', loop: 'Loop', song: 'Song' };
export const KIND_PLURAL: Record<AudioKind, string> = { oneshot: 'One-shots', loop: 'Loops', song: 'Songs' };
export const KINDS: AudioKind[] = ['oneshot', 'loop', 'song'];

/** The guess alone, from the length and (when loaded) the beat map. */
export function guessKind(durationSec: number, map?: TestTrackBeatmap | null): AudioKind {
  if (durationSec > 0 && durationSec < 10) return 'oneshot';
  if (!map) return durationSec <= 60 ? 'loop' : 'song';
  const steady = (map.confidence ?? 1) >= 0.5;
  if (!steady) return durationSec < 30 ? 'oneshot' : 'song';
  const bar = barSeconds(map);
  const bars = (durationSec - map.bar1_sec) / Math.max(1e-6, bar);
  const whole = Math.round(bars);
  const onDownbeat = map.bar1_sec < 0.15;
  if (durationSec <= 60 && onDownbeat && [1, 2, 4, 8, 16, 32].includes(whole) && Math.abs(bars - whole) < 0.08) return 'loop';
  return durationSec <= 45 ? 'loop' : 'song';
}

/** The operator's type, else the guess. */
export function kindOf(t: Pick<TestTrack, 'kind' | 'durationSec'>, map?: TestTrackBeatmap | null): AudioKind {
  return t.kind ?? guessKind(t.durationSec, map);
}
